import { ConfigProvider, App as AntApp } from "antd";
import uzUZ from "antd/locale/uz_UZ";
import dayjs from "dayjs";
import "dayjs/locale/uz-latn";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { brand, theme } from "./theme.ts";
import { queryClient } from "./api.ts";
import { AuthProvider, useAuth } from "./auth.tsx";
import { AppLayout } from "../components/AppLayout.tsx";
import { LoginPage } from "../pages/LoginPage.tsx";
import { ForgotPasswordPage } from "../pages/ForgotPasswordPage.tsx";
import { ResetPasswordPage } from "../pages/ResetPasswordPage.tsx";
import { FamiliesPage } from "../pages/FamiliesPage.tsx";
import { FamilyDetailPage } from "../pages/FamilyDetailPage.tsx";
import { StudentDetailPage } from "../pages/StudentDetailPage.tsx";
import { UsersPage } from "../pages/UsersPage.tsx";
import { SecurityEventsPage } from "../pages/SecurityEventsPage.tsx";
import { AcademicYearsPage } from "../pages/AcademicYearsPage.tsx";
import { SubjectsCoursesPage } from "../pages/SubjectsCoursesPage.tsx";
import { ClassesPage } from "../pages/ClassesPage.tsx";
import { ClassDetailPage } from "../pages/ClassDetailPage.tsx";
import { ClassTimetablePage } from "../pages/ClassTimetablePage.tsx";
import { TeachersPage } from "../pages/TeachersPage.tsx";
import { StaffPage } from "../pages/StaffPage.tsx";
import { LeavingReasonsPage } from "../pages/LeavingReasonsPage.tsx";
import { RolloverPage } from "../pages/RolloverPage.tsx";
import { MessageTemplatesPage } from "../pages/MessageTemplatesPage.tsx";
import { DeliveryLogPage } from "../pages/DeliveryLogPage.tsx";
import { AnnouncementsPage } from "../pages/AnnouncementsPage.tsx";
import { TeacherMessagesPage } from "../pages/TeacherMessagesPage.tsx";
import { TelegramReachPage } from "../pages/TelegramReachPage.tsx";
import { AttendanceReportPage } from "../pages/AttendanceReportPage.tsx";
import { GradingSettingsPage } from "../pages/GradingSettingsPage.tsx";
import { ExamsPage } from "../pages/ExamsPage.tsx";
import { ExamDetailPage } from "../pages/ExamDetailPage.tsx";
import { ExcuseRequestsPage } from "../pages/ExcuseRequestsPage.tsx";
import { BillingRulesPage } from "../pages/BillingRulesPage.tsx";
import { UnmarkedLessonsPage } from "../pages/UnmarkedLessonsPage.tsx";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, initializing } = useAuth();
  const { t } = useTranslation();
  if (initializing) {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: "100vh", background: brand.cream }}>
        <Spin size="large" description={t("app.loading")} />
      </div>
    );
  }
  return user ? <>{children}</> : <Navigate to="/login" replace />;
}

/** Hides a route from users without the permission (any one of several); the API enforces it too, this only avoids a dead screen. */
function RequirePermission({ code, children }: { code: string | readonly string[]; children: React.ReactNode }) {
  const { hasPermission } = useAuth();
  const codes = typeof code === "string" ? [code] : code;
  return codes.some((c) => hasPermission(c)) ? <>{children}</> : <Navigate to="/" replace />;
}

dayjs.locale("uz-latn");

export function App() {
  return (
    <ConfigProvider theme={theme} locale={uzUZ}>
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route
                  element={
                    <RequireAuth>
                      <AppLayout />
                    </RequireAuth>
                  }
                >
                  <Route index element={<Navigate to="/families" replace />} />
                  <Route path="/families" element={<FamiliesPage />} />
                  <Route path="/families/:id" element={<FamilyDetailPage />} />
                  <Route path="/students/:id" element={<StudentDetailPage />} />
                  <Route
                    path="/teachers"
                    element={
                      <RequirePermission code="people.read">
                        <TeachersPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/staff"
                    element={
                      <RequirePermission code="people.read">
                        <StaffPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/leaving-reasons"
                    element={
                      <RequirePermission code="people.read">
                        <LeavingReasonsPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/telegram/unreachable"
                    element={
                      <RequirePermission code="people.read">
                        <TelegramReachPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/rollover"
                    element={
                      <RequirePermission code="people.manage">
                        <RolloverPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/users"
                    element={
                      <RequirePermission code="identity.manage">
                        <UsersPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/security-events"
                    element={
                      <RequirePermission code="audit.read">
                        <SecurityEventsPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/notifications/templates"
                    element={
                      <RequirePermission code="notifications.manage">
                        <MessageTemplatesPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/notifications/log"
                    element={
                      <RequirePermission code="notifications.manage">
                        <DeliveryLogPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/announcements"
                    element={
                      <RequirePermission code="announcements.read">
                        <AnnouncementsPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/attendance/excuse-requests"
                    element={
                      <RequirePermission code="attendance.excuse">
                        <ExcuseRequestsPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/attendance/unmarked"
                    element={
                      <RequirePermission code="attendance.readAll">
                        <UnmarkedLessonsPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/teacher-messages"
                    element={
                      <RequirePermission code={["teacherMessages.send", "teacherMessages.read"]}>
                        <TeacherMessagesPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/reports/attendance"
                    element={
                      <RequirePermission code="reports.read">
                        <AttendanceReportPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/grading/settings"
                    element={
                      <RequirePermission code="grading.readAll">
                        <GradingSettingsPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/grading/exams"
                    element={
                      <RequirePermission code="grading.read">
                        <ExamsPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/grading/exams/:id"
                    element={
                      <RequirePermission code="grading.read">
                        <ExamDetailPage />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="/billing/rules"
                    element={
                      <RequirePermission code="billing.read">
                        <BillingRulesPage />
                      </RequirePermission>
                    }
                  />
                  <Route path="/academic/years" element={<AcademicYearsPage />} />
                  <Route path="/academic/subjects" element={<SubjectsCoursesPage />} />
                  <Route path="/academic/classes" element={<ClassesPage />} />
                  <Route path="/academic/classes/:id" element={<ClassDetailPage />} />
                  <Route path="/academic/classes/:id/timetable" element={<ClassTimetablePage />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  );
}
